/**
 * DocumentInput — Text area + file upload for starting an editor session
 */

import { useState, useCallback, useRef } from 'react'
import { Upload, FileText, X } from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'
import type { EditorQualityTier } from '@/lib/api'

interface DocumentInputProps {
  onSessionCreated: (sessionId: string) => void
}

const QUALITY_TIERS: { value: EditorQualityTier; label: string; description: string }[] = [
  { value: 'highest', label: 'Highest', description: 'Opus for all editors' },
  { value: 'high', label: 'High', description: 'Sonnet for all editors' },
  { value: 'balanced', label: 'Balanced', description: 'Sonnet + Haiku for grammar' },
  { value: 'economy', label: 'Economy', description: 'Haiku for all editors' },
]

export function DocumentInput({ onSessionCreated }: DocumentInputProps) {
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [tier, setTier] = useState<EditorQualityTier>('balanced')
  const [creating, setCreating] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(async (file: File) => {
    try {
      const result = await api.editor.extractText(file)
      setText(result.text)
      if (!title) {
        setTitle(file.name.replace(/\.[^.]+$/, ''))
      }
      notify.success(`Extracted ${result.text.split(/\s+/).length} words from ${result.filename}`)
    } catch (err) {
      notify.error('Failed to extract text', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [title])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleCreate = async () => {
    if (!text.trim()) return
    setCreating(true)
    try {
      const session = await api.editor.sessions.create({
        documentText: text,
        documentTitle: title || undefined,
        qualityTier: tier,
      })
      onSessionCreated(session.id)
      notify.success('Editor session created')
    } catch (err) {
      notify.error('Failed to create session', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setCreating(false)
    }
  }

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">AI Editor Pipeline</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Upload or paste your document, then run any of 7 specialized AI editors in any order.
        </p>
      </div>

      {/* Title input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Document Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My Manuscript..."
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-surface-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Drop zone + textarea */}
      <div
        className={`relative rounded-lg border-2 border-dashed transition-colors ${
          dragActive
            ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
            : 'border-gray-300 dark:border-white/20'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your document text here, or drag & drop a file..."
          rows={16}
          className="w-full px-4 py-3 rounded-lg bg-transparent text-gray-900 dark:text-white text-sm font-mono resize-y focus:outline-none"
        />

        {/* Drag overlay */}
        {dragActive && (
          <div className="absolute inset-0 flex items-center justify-center bg-indigo-50/90 dark:bg-indigo-900/80 rounded-lg">
            <div className="text-center">
              <Upload className="w-10 h-10 text-indigo-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Drop file to extract text</p>
            </div>
          </div>
        )}
      </div>

      {/* File upload button + word count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Upload File
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.md,.docx,.pdf,.rtf,.html"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
            }}
            className="hidden"
          />
          {text && (
            <button
              onClick={() => { setText(''); setTitle('') }}
              className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {wordCount > 0 ? `${wordCount.toLocaleString()} words` : 'No text yet'}
        </span>
      </div>

      {/* Quality tier selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Quality Tier
        </label>
        <div className="grid grid-cols-4 gap-2">
          {QUALITY_TIERS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTier(t.value)}
              className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                tier === t.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-500'
                  : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/20'
              }`}
            >
              <div>{t.label}</div>
              <div className="text-[10px] opacity-60 mt-0.5">{t.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Create session button */}
      <div className="flex justify-center">
        <button
          onClick={handleCreate}
          disabled={!text.trim() || creating}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-medium text-sm rounded-lg transition-colors"
        >
          {creating ? (
            <>Creating Session...</>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              Start Editor Session
            </>
          )}
        </button>
      </div>
    </div>
  )
}
